import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AuthShell, Field } from "./AuthShell";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/common/PasswordInput";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    restaurant_name: "", owner_name: "", email: "", password: "", confirm: "", phone: "", address: "", city: "", state: "", pincode: "", cuisine: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (Object.entries(form).some(([k, v]) => k !== "cuisine" && !v.trim())) return toast.error("Please fill in all fields");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return toast.error("Enter a valid email address");
    if (!/^(\+?91|0)?[6-9]\d{9}$/.test(form.phone.replace(/[\s-]/g, ""))) return toast.error("Enter a valid 10-digit Indian mobile number");
    if (!/^[1-9]\d{5}$/.test(form.pincode)) return toast.error("Enter a valid 6-digit PIN code");
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    if (form.password !== form.confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      const { confirm, ...payload } = form;
      await register(payload);
      toast.success("Restaurant registered! Your account is pending admin approval.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err, "Could not register"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Create your restaurant"
      subtitle="Set up your account in a minute. Approval is quick."
      footer={<>Already have an account? <Link to="/login" className="font-semibold text-emerald-600 hover:underline">Sign in</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Field label="Restaurant name">
          <Input data-testid="reg-restaurant-name" required value={form.restaurant_name} onChange={set("restaurant_name")} placeholder="Spice Junction" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Your name">
            <Input data-testid="reg-owner-name" required value={form.owner_name} onChange={set("owner_name")} placeholder="Priya Sharma" />
          </Field>
          <Field label="Cuisine (optional)">
            <Input data-testid="reg-cuisine" value={form.cuisine} onChange={set("cuisine")} placeholder="North Indian" />
          </Field>
        </div>
        <Field label="Email">
          <Input data-testid="reg-email" type="email" required value={form.email} onChange={set("email")} placeholder="owner@restaurant.com" />
        </Field>
        <Field label="Password">
          <PasswordInput data-testid="reg-password" required value={form.password} onChange={set("password")} placeholder="At least 8 characters" />
        </Field>
        <Field label="Confirm password">
          <PasswordInput data-testid="reg-confirm-password" required value={form.confirm} onChange={set("confirm")} placeholder="Re-enter password" />
        </Field>
        <Field label="Mobile number">
          <Input data-testid="reg-phone" type="tel" inputMode="numeric" required value={form.phone} onChange={set("phone")} placeholder="98765 43210" />
        </Field>
        <Field label="Full address">
          <Textarea data-testid="reg-address" required value={form.address} onChange={set("address")} rows={2} placeholder="Shop no., street, area" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="City"><Input data-testid="reg-city" required value={form.city} onChange={set("city")} placeholder="Pune" /></Field>
          <Field label="State"><Input data-testid="reg-state" required value={form.state} onChange={set("state")} placeholder="Maharashtra" /></Field>
          <Field label="PIN code"><Input data-testid="reg-pincode" required inputMode="numeric" maxLength={6} value={form.pincode} onChange={set("pincode")} placeholder="411001" /></Field>
        </div>
        <Button data-testid="register-submit" type="submit" disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-600">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create account
        </Button>
      </form>
    </AuthShell>
  );
}
